import { ConfigService } from './config.service';
import http from './http.service';

/**
 * Shape of `GET /custom-domain/public/resolve`. Declared here rather than
 * generated because the route is unauthenticated, so the backend publishes it
 * in its public OpenAPI document, which `pnpm gen:api` does not read.
 */
interface ResolvedEndpoints {
  platform: string;
  api_url: string;
  socket_url: string;
}

interface CachedEndpoints extends ResolvedEndpoints {
  host: string;
  resolved_at: number;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

// `storageKey` is not used on purpose: it reads the app id through
// ConfigService, and the key has to be readable before anything else runs.
const cacheKey = (): string =>
  `${ConfigService.getInstance().getBuildValue('appId')}.runtime.endpoints`;

/**
 * True on the platform's own hosts: the host the bundle was built for, local
 * development and Cloudflare preview deployments. Everywhere else the
 * dashboard is being served from an organization's custom domain.
 */
export const isPlatformHost = (): boolean => {
  const { hostname, host } = window.location;
  if (LOCAL_HOSTS.has(hostname) || hostname.endsWith('.pages.dev')) return true;
  try {
    const built = ConfigService.getInstance().getBuildValue('loginPageUrl');
    return new URL(built).host === host;
  } catch {
    return false;
  }
};

const readCache = (): CachedEndpoints | null => {
  try {
    const raw = window.localStorage.getItem(cacheKey());
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedEndpoints;
    const fresh = Date.now() - cached.resolved_at < CACHE_TTL_MS;
    return cached.host === window.location.host && fresh ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (value: ResolvedEndpoints | null): void => {
  try {
    if (value) {
      const cached: CachedEndpoints = {
        ...value,
        host: window.location.host,
        resolved_at: Date.now(),
      };
      window.localStorage.setItem(cacheKey(), JSON.stringify(cached));
    } else {
      window.localStorage.removeItem(cacheKey());
    }
  } catch {
    // Blocked storage only costs the next load a round trip.
  }
};

const apply = (value: ResolvedEndpoints): void => {
  ConfigService.getInstance().applyRuntimeOverrides({
    apiUrl: value.api_url,
    socketUrl: value.socket_url,
  });
};

/**
 * Synchronous half, run before anything issues a request: on a custom domain
 * the login page is this origin (Google OAuth and impersonation links come
 * back here), and the API resolved on a previous visit applies immediately.
 */
export const applyRuntimeEndpoints = (): void => {
  if (isPlatformHost()) return;
  ConfigService.getInstance().applyRuntimeOverrides({
    loginPageUrl: `${window.location.origin}/auth/login`,
  });
  const cached = readCache();
  if (cached) apply(cached);
};

/**
 * Asynchronous half: asks the platform's own API which API this custom
 * domain belongs to. Until it answers, requests keep going to the platform's
 * API, which accepts this origin too, so nothing waits on it.
 */
export const refreshRuntimeEndpoints = async (): Promise<void> => {
  if (isPlatformHost()) return;
  const config = ConfigService.getInstance();
  try {
    const response = await http.get<ResolvedEndpoints>('/custom-domain/public/resolve', {
      baseURL: config.getBuildValue('apiUrl'),
      params: { host: window.location.host },
    });
    apply(response.data);
    writeCache(response.data);
  } catch {
    // Not (or no longer) a verified custom domain: fall back to the platform's API.
    config.clearRuntimeOverrides(['apiUrl', 'socketUrl']);
    writeCache(null);
  }
};
